import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Card,
  Col,
  Row,
  Table,
  Select,
  Button,
  message,
  Tag,
  Typography,
  Divider,
} from "antd";
import {
  ShoppingOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  selectIsOrderLoaded,
  selectIsOrderLoading,
  selectOrder,
  selectOrderItems,
} from "../../../redux-toolkit/order/order-selector";
import {
  fetchOrderById,
  fetchOrderItemsByOrderId,
} from "../../../redux-toolkit/order/order-thunks";
import { resetOrderState } from "../../../redux-toolkit/order/order-slice";
import ContentTitle from "../../../components/ContentTitle/ContentTitle";
import Spinner from "../../../components/Spinner/Spinner";
import { OrderItemResponse } from "../../../types/types";
import { BASE_URL } from "../../../constants/urlConstants";
import { selectUserFromUserState } from "../../../redux-toolkit/user/user-selector";
import "./ManageUserOrder.css";

const { Option } = Select;
const { Title, Text } = Typography;

const ManageUserOrder: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();
  const order = useSelector(selectOrder);
  const orderItems = useSelector(selectOrderItems);
  const isOrderLoading = useSelector(selectIsOrderLoading);
  const isOrderLoaded = useSelector(selectIsOrderLoaded);
  const user = useSelector(selectUserFromUserState);

  const {
    id,
    email,
    firstName,
    lastName,
    totalPrice,
    postIndex,
    phoneNumber,
    date,
    city,
    address,
    paymentType,
    transactionId,
    deliveryStatus,
  } = order;

  const [status, setStatus] = useState<string>(deliveryStatus || "ORDERED");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const isAdmin = user?.roles?.includes("ADMIN");

  useEffect(() => {
    dispatch(fetchOrderById(params.id));
    return () => {
      dispatch(resetOrderState());
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (isOrderLoaded) {
      dispatch(fetchOrderItemsByOrderId(params.id));
      setStatus(order.deliveryStatus || "ORDERED");
    }
  }, [isOrderLoaded, order]);

  const handleStatusUpdate = async () => {
    if (!isAdmin) {
      message.warning("Only admin users can update order status.");
      return;
    }

    setIsUpdating(true);
    try {
      const response = await fetch(`${BASE_URL}/api/v1/order/${id}/status?status=${status}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error("Failed to update order status");
      message.success(`Delivery status updated to "${status}"`);
      dispatch(fetchOrderById(params.id));
    } catch (error) {
      console.error(error);
      message.error("Error updating delivery status");
    } finally {
      setIsUpdating(false);
    }
  };

  const statusColorMap: Record<string, string> = {
    ORDERED: "default",
    PROCESSING: "blue",
    SHIPPED: "orange",
    DELIVERED: "green",
    CANCELLED: "red",
  };

  if (isOrderLoading) return <Spinner />;

  return (
    <div style={{ background: "#f1f3f6", padding: "24px 40px", minHeight: "100vh" }}>
      <Card
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={4} style={{ marginBottom: 0 }}>
              <ShoppingOutlined style={{ marginRight: 8 }} />
              Order #{id}
            </Title>
            <Text type="secondary">Placed on {date}</Text>
          </Col>

          <Col className="print-hide">
            <Button
              type="primary"
              onClick={() => window.open(`/account/user/order/invoice/${id}`, "_blank")}
              style={{ marginRight: 12 }}
            >
              🧾 Print Invoice
            </Button>

            <Tag
              color={statusColorMap[deliveryStatus || "ORDERED"]}
              style={{ fontSize: 14, padding: "4px 12px" }}
            >
              {deliveryStatus || "ORDERED"}
            </Tag>
          </Col>
        </Row>

        <Divider />


        {/* 🧍 Customer Info */}
        <Row gutter={32}>
          <Col xs={24} md={12}>
            <Title level={5}>
              <InfoCircleOutlined style={{ marginRight: 6 }} />
              Customer Details
            </Title>
            <div className="info-block">
              <p><b>Name:</b> {firstName} {lastName}</p>
              <p><b>Email:</b> {email}</p>
              <p><b>Phone:</b> {phoneNumber}</p>
              <p><b>Address:</b> {address}, {city}</p>
              <p><b>Postcode:</b> {postIndex}</p>
            </div>
          </Col>

          {/* 📦 Order Info */}
          <Col xs={24} md={12}>
            <Title level={5}>
              <InfoCircleOutlined style={{ marginRight: 6 }} />
              Order Details
            </Title>
            <div className="info-block">
              <p><b>Payment Type:</b> {paymentType || "N/A"}</p>
              <p><b>Transaction ID:</b> {transactionId || "-"}</p>
              <p><b>Total:</b> ₹{totalPrice}.00</p>
            </div>

            {isAdmin && (
              <div style={{ marginTop: 16 }}>
                <label
                  style={{
                    fontWeight: 600,
                    display: "block",
                    marginBottom: 8,
                  }}
                >
                  Update Delivery Status
                </label>
                <Select
                  value={status}
                  onChange={(value) => setStatus(value)}
                  style={{ width: "60%", marginRight: 8 }}
                >
                  <Option value="ORDERED">Ordered</Option>
                  <Option value="PROCESSING">Processing</Option>
                  <Option value="SHIPPED">Shipped</Option>
                  <Option value="DELIVERED">Delivered</Option>
                  <Option value="CANCELLED">Cancelled</Option>
                </Select>

                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={isUpdating}
                  onClick={handleStatusUpdate}
                >
                  Update
                </Button>
              </div>
            )}
          </Col>
        </Row>

        <Divider />

        {/* 🛍️ Order Items Table */}
        <Title level={5} style={{ marginBottom: 16 }}>
          Items in this Order
        </Title>
        <Table
          rowKey="id"
          pagination={false}
          dataSource={orderItems}
          bordered
          style={{ borderRadius: 10 }}
          columns={[
            {
              title: "Product ID",
              dataIndex: "id",
              key: "id",
              width: 100,
            },
            {
              title: "Brand",
              render: (_, order: OrderItemResponse) => order.perfume.brand,
            },
            {
              title: "Name",
              render: (_, order: OrderItemResponse) => order.perfume.perfumeTitle,
            },
            {
              title: "Quantity",
              dataIndex: "quantity",
              key: "quantity",
              width: 100,
            },
            {
              title: "Price (₹)",
              render: (_, order: OrderItemResponse) => order.perfume.price,
            },
            {
              title: "Amount (₹)",
              render: (_, order: OrderItemResponse) => order.amount,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default ManageUserOrder;
